import { useMemo, useState } from 'react'

import { createPortal } from 'react-dom'

import { classNames } from '@/utils/helpers.ts'

interface Props {
  startTime: Date
  endTime: Date
  isConflicting?: boolean
  isSelected?: boolean
}

function calculateRotation(time: Date): number {
  const minutes = time.getHours() * 60 + time.getMinutes()

  return (minutes / 24 / 60) * 360
}

export const TimeArc = ({
  startTime,
  endTime,
  isConflicting,
  isSelected,
}: Props) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })

  const startAngle = calculateRotation(startTime)
  const endAngle = calculateRotation(endTime)

  // Handle cases when interval crosses midnight or has zero duration
  let angleSpan

  if (startTime.getTime() === endTime.getTime()) {
    angleSpan = 2
  } else {
    angleSpan =
      endAngle <= startAngle
        ? endAngle + 360 - startAngle
        : endAngle - startAngle
    angleSpan = Math.max(angleSpan, 3)
  }

  const radius = 100
  const center = 100

  // Convert angles to radians
  const startRad = ((startAngle - 90) * Math.PI) / 180
  const endRad = ((startAngle + angleSpan - 90) * Math.PI) / 180

  const x1 = center + radius * Math.cos(startRad)
  const y1 = center + radius * Math.sin(startRad)
  const x2 = center + radius * Math.cos(endRad)
  const y2 = center + radius * Math.sin(endRad)

  const largeArcFlag = angleSpan > 180 ? 1 : 0

  const pathData =
    `M ${x1} ${y1} ` + // M = Move to (x1,y1) - starting point of the arc
    `A ${radius} ${radius} ` + // A = Arc command, X and Y radius of the arc
    `0 ` + // Rotation of the arc (not needed)
    `${largeArcFlag} ` + // Large arc flag (1 if arc > 180°, 0 if less)
    `1 ` + // Sweep flag (1 = clockwise)
    `${x2} ${y2}` // End point of the arc (x2,y2)

  const tooltipText = useMemo(() => {
    const formatTime = (date: Date) => {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    }

    return `${formatTime(startTime)} → ${formatTime(endTime)}`
  }, [startTime, endTime])

  const handleMouseMove = (event: React.MouseEvent) => {
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY,
    })
  }

  const handleMouseEnter = () => {
    setShowTooltip(true)
  }

  const handleMouseLeave = () => {
    setShowTooltip(false)
  }

  return (
    <>
      <g
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        <path
          d={pathData}
          fill="none"
          strokeWidth={6}
          className={classNames('cursor-pointer transition-colors', {
            'stroke-blue-500 opacity-30': !isSelected && !isConflicting,
            'stroke-blue-500': isSelected && !isConflicting,
            'stroke-red-500 opacity-80': isConflicting,
          })}
        />
      </g>
      {showTooltip &&
        createPortal(
          <div
            className="bg-secondary-500 fixed z-[100] rounded-lg border border-gray-200 p-2 text-xs shadow-lg sm:p-3 whitespace-nowrap pointer-events-none"
            style={{
              left: tooltipPosition.x + 10,
              top: tooltipPosition.y - 30,
            }}
          >
            {tooltipText}
          </div>,
          document.body,
        )}
    </>
  )
}
